import { useState, useEffect, useRef } from 'react'
import ActorPicker from '../ActorPicker'
import PartyRevealScreen from './PartyRevealScreen'
import PartyReactionTest from './PartyReactionTest'
import PartyCoopGameplay from './PartyCoopGameplay'
import PartyCoopDone from './PartyCoopDone'
import PartyTeamBuilder from './PartyTeamBuilder'
import PartyH2HGameplay from './PartyH2HGameplay'
import PartyH2HDone from './PartyH2HDone'
import PartyDCDirectorSelect from './PartyDCDirectorSelect'
import PartyDCCasting from './PartyDCCasting'
import PartyDCGameplay from './PartyDCGameplay'
import PartyDCDone from './PartyDCDone'
import PartyInstructions from './PartyInstructions'
import PartyTrilogyStandings from './PartyTrilogyStandings'
import PartyTrilogyGameplay from './PartyTrilogyGameplay'
import PartyTrilogyFinal from './PartyTrilogyFinal'
import Confetti from '../Confetti'
import { updateParty, assignActorPickers } from '../../utils/partyGame'
import { getActorMovies } from '../../api/tmdb'
import { requestNotificationPermission, notifyIfBackground } from '../../utils/notifications'
import { MODE_NAMES, MODE_ICONS } from './modeMeta'
import {
  IconCrown, IconCheck, IconTicket, IconPlay, IconTrophy, IconArrowRight, IconHome,
  IconClapper, IconClock,
} from '../icons'

function fmtTime(secs) {
  if (secs == null) return '--:--'
  const m = String(Math.floor(secs / 60)).padStart(2, '0')
  const s = String(secs % 60).padStart(2, '0')
  return `${m}:${s}`
}

const MODE_DESCRIPTIONS = {
  'battle-royale': [
    'Connect the start actor to the goal actor',
    'Fewest hops wins — time breaks ties',
    'Every player races simultaneously',
  ],
  'speed-run': [
    'Connect the start actor to the goal actor',
    "First to finish wins — hops don't matter",
    'Every player races simultaneously',
  ],
  'h2h': [
    'Teams compete head to head',
    'Fewest combined team hops wins',
    'Time breaks ties',
  ],
  'co-op': [
    'Everyone works together as one team',
    'Each player takes a turn adding to the chain',
    'No winner — collective score counts',
  ],
  'directors-cut': [
    'Everyone nominates and votes on each move',
    'Most votes decides the next actor or movie',
    'No timer — pure group decision making',
  ],
  'trilogy': [
    'Three rounds, three puzzles',
    'Two random players pick the start and goal actors each round',
    'Fewest total hops across all three rounds wins',
    'Time breaks ties — lowest total time takes the crown',
  ],
}

const TEAM_COLORS = ['#e05c5c', '#5c9ee0', '#5ce08a', '#e0c05c', '#c05ce0', '#5ce0d8']

export default function PartyPlayerScreen({ party, currentUserId, onLeave, onBackToLobby, onConfirmReady, onGameStart, onRefundMyTicket }) {
  const [selectedTeamCount, setSelectedTeamCount] = useState(null)
  const [teamNames, setTeamNames] = useState({})
  const [actorPickDone, setActorPickDone] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [celebrating, setCelebrating] = useState(false)
  const [showLobby, setShowLobby] = useState(false)   // "Back to Lobby" override on a results screen
  const gameStartedRef = useRef(false)
  const assignmentFiredRef = useRef(false)
  const [now, setNow] = useState(() => Date.now())   // Ready Up countdown tick

  // Fire confetti on every player device the moment status flips to 'coop-results'.
  // PartyCoopDone also renders its own confetti so the burst persists across the
  // route transition. Same pattern should be applied to Director's Cut when built.
  useEffect(() => {
    if (party?.status !== 'coop-results' && party?.status !== 'h2h-results' && party?.status !== 'dc-results') return
    setCelebrating(true)
    const id = setTimeout(() => setCelebrating(false), 4000)
    return () => clearTimeout(id)
  }, [party?.status])

  // Ready Up countdown ticker — re-render the pre-start 0:SS each tick.
  useEffect(() => {
    if (party?.status !== 'pre-start') return
    const id = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(id)
  }, [party?.status])

  // When party transitions to 'playing' or 'trilogy-playing', launch the gameplay loop
  useEffect(() => {
    if (
      (party?.status === 'playing' || party?.status === 'trilogy-playing') &&
      !gameStartedRef.current &&
      onGameStart &&
      party.startActor &&
      party.goalActor
    ) {
      gameStartedRef.current = true
      onGameStart(party.startActor, party.goalActor)
    }
  }, [party?.status, party?.startActor, party?.goalActor, onGameStart])

  // ── Game-start readiness ack ────────────────────────────────────────────────
  // During the LCA/countdown window, prefetch this round's gameplay data and then
  // set players.{uid}.lcaReady = true. The host holds everyone on the LCA screen
  // until every confirmed player has acked (or its timeout elapses), so no device
  // is stranded on "Lights… Camera… Action!" after the others start. The host
  // clears lcaReady when it (re)opens the countdown, so each round re-acks. If the
  // prefetch fails we intentionally do NOT ack — the host's timeout covers it, and
  // gameplay will retry the fetch on mount.
  useEffect(() => {
    const me = party?.players?.[currentUserId]
    if (party?.status !== 'countdown' || me?.ready !== true || me?.lcaReady === true) return
    let cancelled = false
    ;(async () => {
      const ok = await prefetchGameplayData(party)
      if (cancelled || !ok) return
      try {
        await updateParty(party.id, { [`players.${currentUserId}.lcaReady`]: true })
      } catch { /* a failed ack just means the host waits / times out */ }
    })()
    return () => { cancelled = true }
  }, [
    party?.status,
    party?.id,
    currentUserId,
    party?.players?.[currentUserId]?.ready,
    party?.players?.[currentUserId]?.lcaReady,
    party?.startActor?.id,
  ])

  // Reset state when party returns to lobby or starts a new Trilogy round
  useEffect(() => {
    if (party?.status === 'lobby' || party?.status === 'actor_pick') {
      gameStartedRef.current = false
      setActorPickDone(false)
      assignmentFiredRef.current = false
    }
    if (party?.status === 'lobby') {
      setConfirming(false)
    }
  }, [party?.status])

  // "Back to Lobby" override — when a waiting player taps it on a results
  // screen, show the lobby/waiting view regardless of party.status. Reset on
  // any status change so a new round auto-returns them to the correct screen.
  useEffect(() => { setShowLobby(false) }, [party?.status])
  const handleBackToLobby = () => {
    onBackToLobby?.()      // reset local game state (the App.jsx handler)
    // On a finished game the App.jsx handler exits to home; only show the
    // waiting-for-host lobby between rounds (round_standings).
    if (party?.status !== 'final_standings') setShowLobby(true)
  }

  // Backup assignment watcher: runs only on picker 0's device.
  // Fires when picker 0 went first and now pick1 has arrived via Firestore.
  // Prevents the reveal from stalling if picker 1's inline handler didn't fire.
  useEffect(() => {
    if (party?.status !== 'actor_pick') return
    if (!party?.pick0 || !party?.pick1) return
    if (party?.startActor) return            // already assigned
    if (assignmentFiredRef.current) return
    if (party?.actorPickerIds?.[0] !== currentUserId) return // only picker 0 runs this
    assignmentFiredRef.current = true
    const coin = Math.random() > 0.5
    updateParty(party.id, {
      startActor: coin ? party.pick0 : party.pick1,
      goalActor:  coin ? party.pick1 : party.pick0,
      pick0: null,
      pick1: null,
      status: 'reveal',
    }).catch(() => {})
  }, [party?.pick0, party?.pick1, party?.startActor, party?.status, currentUserId, party?.actorPickerIds, party?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Stamp lastSeenResults on the player's Firestore entry the first time
  // the results screen is shown (BR/SR 'results', Co-Op 'coop-results', H2H 'h2h-results').
  // Recovery will skip this party on next load.
  const resultsSeenRef = useRef(false)
  useEffect(() => {
    const isResultsScreen = party?.status === 'results'
                         || party?.status === 'coop-results'
                         || party?.status === 'h2h-results'
                         || party?.status === 'dc-results'
    if (!isResultsScreen || resultsSeenRef.current || !currentUserId || !party?.id) return
    resultsSeenRef.current = true
    updateParty(party.id, { [`players.${currentUserId}.lastSeenResults`]: new Date() }).catch(() => {})
  }, [party?.status]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Notifications: request once on entering the party ──────────────────────
  const notifRequestedRef = useRef(false)
  useEffect(() => {
    if (!party || notifRequestedRef.current) return
    notifRequestedRef.current = true
    requestNotificationPermission().catch(() => {})
  }, [party])

  // ── Notifications: fire on key events (only when tab unfocused) ────────────
  const lastNotifiedStatusRef = useRef(null)
  useEffect(() => {
    if (!party || !currentUserId) return
    const status = party.status
    if (lastNotifiedStatusRef.current === status) return
    lastNotifiedStatusRef.current = status

    if (status === 'actor_pick') {
      const isMyTurn = party.actorPickerIds?.includes(currentUserId)
      if (isMyTurn) {
        notifyIfBackground(
          '🎬 Your turn!',
          "It's your turn to pick an actor!",
          { tag: 'party-turn' },
        )
      }
    } else if (status === 'countdown') {
      notifyIfBackground(
        '🎬 Lights... Camera... Action!',
        'The game is starting!',
        { tag: 'party-countdown' },
      )
    } else if (status === 'reaction_test') {
      notifyIfBackground(
        '🎬 Reaction Test!',
        'Tap fast when the clapperboard drops!',
        { tag: 'party-reaction' },
      )
    } else if (status === 'results' || status === 'coop-results') {
      notifyIfBackground(
        '🏆 Results are in!',
        'See how everyone did.',
        { tag: 'party-results' },
      )
    }
  }, [party?.status, party?.actorPickerIds, currentUserId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Co-Op: notify when the torch passes to this player
  const lastTurnUidRef = useRef(null)
  useEffect(() => {
    if (party?.status !== 'co-op-playing') { lastTurnUidRef.current = null; return }
    const turnOrder = party.turnOrder ?? []
    const turnIndex = party.currentTurnIndex ?? 0
    const activeUid = turnOrder[turnIndex]
    if (!activeUid) return
    if (lastTurnUidRef.current === activeUid) return
    lastTurnUidRef.current = activeUid
    if (activeUid === currentUserId) {
      notifyIfBackground("🎬 It's your turn!", 'The cast is waiting on you.', { tag: 'party-coop-turn' })
    }
  }, [party?.status, party?.currentTurnIndex, party?.turnOrder, currentUserId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!party) {
    return (
      <div className="party-player-screen">
        <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
      </div>
    )
  }

  const isDirector = currentUserId === party.directorId
  const players = party.players ?? {}
  const playerEntries = Object.entries(players)

  // ── STATUS: lobby ──────────────────────────────────────────────────────────
  if (showLobby || party.status === 'lobby') {
    const LobbyModeIcon = MODE_ICONS[party.mode]
    return (
      <div className="party-player-screen">
        <p className="party-player-screen__headline">You're in!</p>
        <p className="party-player-screen__code">{party.code}</p>
        <p className="party-player-screen__mode">
          {LobbyModeIcon && <LobbyModeIcon size={15} aria-hidden="true" />}
          <span>{MODE_NAMES[party.mode] || party.mode}</span>
        </p>
        <p className="party-player-screen__pulse">Waiting for host to start...</p>

        <div className="party-player-section" style={{ marginTop: 16 }}>
          <div className="party-player-grid">
            {playerEntries.map(([uid, p]) => {
              const initial = (p.username || '?').charAt(0).toUpperCase()
              return (
                <div
                  key={uid}
                  className={`party-player-chip${p.isHost ? ' party-player-chip--host' : ''}`}
                >
                  <div className="party-player-chip__avatar">
                    {p.avatar
                      ? <img src={p.avatar} alt={p.username} />
                      : <span className="party-avatar__initial">{initial}</span>}
                  </div>
                  <span className="party-player-chip__name">
                    <span>{p.username || 'Guest'}</span>
                    {p.isHost && (
                      <span className="party-crown-badge" aria-label="Host">
                        <IconCrown size={12} />
                      </span>
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <button className="btn-ghost" onClick={onLeave} style={{ marginTop: 16, fontSize: '0.8rem' }}>
          Leave Party
        </button>
      </div>
    )
  }

  // ── STATUS: pre-start — Ready Up! ──────────────────────────────────────────
  if (party.status === 'pre-start') {
    const myReady = players[currentUserId]?.ready === true

    const handleConfirm = async () => {
      if (confirming) return
      setConfirming(true)
      try {
        await onConfirmReady?.()
      } catch {
        // onConfirmReady surfaces its own errors; the finally re-enables retry.
      } finally {
        // Always clear the spinner — even when onConfirmReady hits a silent
        // double-charge guard (already charged) and returns without setting `ready`.
        setConfirming(false)
      }
    }

    // Countdown synced off the host's stamp (display only — the host drives start).
    const startedMs = party.preStartStartedAt?.toMillis?.()
      ?? (party.preStartStartedAt?.seconds != null ? party.preStartStartedAt.seconds * 1000 : null)
    const remaining = startedMs != null
      ? Math.max(0, Math.ceil(30 - (now - startedMs) / 1000))
      : 30

    return (
      <div className="party-player-screen party-ready-up">
        <div className="party-ready-up__chip" aria-hidden="true">
          <IconClapper size={30} />
        </div>
        <h1 className="party-ready-up__title">Ready Up!</h1>
        <p className="party-ready-up__sub">
          <strong>{MODE_NAMES[party.mode] || 'The game'}</strong> is about to start
        </p>

        <div className="party-ready-up__countdown">
          <IconClock size={34} aria-hidden="true" />
          <span>0:{String(remaining).padStart(2, '0')}</span>
        </div>

        {myReady ? (
          <>
            <div className="party-ready-up__confirmed">
              <IconCheck size={22} aria-hidden="true" />
              <span>You're In! 🎬</span>
            </div>
            <p className="party-ready-up__hint">Waiting on the rest of the cast…</p>
          </>
        ) : (
          <>
            <button
              className="btn-primary party-ready-up__go"
              onClick={handleConfirm}
              disabled={confirming}
            >
              {confirming ? '…' : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <IconPlay size={22} aria-hidden="true" /> Let's Go!
                </span>
              )}
            </button>
            <p className="party-ready-up__cost">Spend 1 ticket to play</p>
            <p className="party-ready-up__hint">Tap to join the round</p>
            <button className="btn-ghost" onClick={onLeave} style={{ marginTop: 4, fontSize: '0.8rem' }}>
              Leave Party
            </button>
          </>
        )}
      </div>
    )
  }

  // ── DROPPED at Ready Up ──────────────────────────────────────────────────────
  // The host started the round (30s timer or "Start Round") before this player
  // tapped "Let's Go" — they weren't charged and aren't in this round. Sit it out;
  // when the host returns to the lobby (ready resets) they rejoin automatically.
  if (
    party.players?.[currentUserId]?.ready !== true &&
    !['lobby', 'pre-start', 'ended', 'results', 'coop-results', 'h2h-results',
      'dc-results', 'round_standings', 'final_standings'].includes(party.status)
  ) {
    return (
      <div className="party-player-screen">
        <p className="party-player-screen__headline">Round starting without you…</p>
        <p className="party-player-screen__pulse">
          You didn't tap in before the clock ran out — you'll be in the next round.
        </p>
        <button className="btn-ghost" onClick={onLeave} style={{ marginTop: 16, fontSize: '0.8rem' }}>
          Leave Party
        </button>
      </div>
    )
  }

  // ── DIRECTOR'S CUT routing ─────────────────────────────────────────────
  // Consolidated DC status block. Every DC status has an explicit return —
  // never fall through to the legacy director_reveal or the raw-text fallback.
  // The role passed to each component is:
  //   - 'director' if this player IS the chosen DC Director (party.directorUid)
  //   - 'player'   for any other player (selection screen reads as "player")
  //   - 'agent'    for any other player (casting/gameplay/results read as "agent")
  // Note these labels differ by component — match what each one expects.
  {
    const dcStatus = party.status
    const isDCDirector = !!party.directorUid && currentUserId === party.directorUid

    if (dcStatus === 'director_selection') {
      // eslint-disable-next-line no-console
      console.log('[DC] route director_selection (player)', { myUid: currentUserId, directorUid: party.directorUid })
      return (
        <PartyDCDirectorSelect
          party={party}
          currentUserId={currentUserId}
          role={isDCDirector ? 'director' : 'player'}
        />
      )
    }

    // For director_reveal, prefer DC routing if we have a directorUid set
    // (the DC reveal phase always populates this field). Fall through to the
    // legacy non-DC director_reveal block below if directorUid is missing.
    if (dcStatus === 'director_reveal' && party.directorUid) {
      return (
        <PartyDCDirectorSelect
          party={party}
          currentUserId={currentUserId}
          role={isDCDirector ? 'director' : 'player'}
        />
      )
    }

    if (dcStatus === 'casting') {
      return (
        <PartyDCCasting
          party={party}
          currentUserId={currentUserId}
          role={isDCDirector ? 'director' : 'agent'}
        />
      )
    }

    if (dcStatus === 'dc-playing') {
      return (
        <PartyDCGameplay
          party={party}
          currentUserId={currentUserId}
          role={isDCDirector ? 'director' : 'agent'}
        />
      )
    }

    if (dcStatus === 'dc-results') {
      return (
        <>
          {celebrating && <Confetti />}
          <PartyDCDone
            party={party}
            currentUserId={currentUserId}
            forHost={false}
            onBackToLobby={handleBackToLobby}
          />
        </>
      )
    }
  }

  // ── STATUS: director_reveal ────────────────────────────────────────────────
  if (party.status === 'director_reveal') {
    if (isDirector) {
      const isBattleRoyale = party.mode === 'battle-royale'
      const handleContinue = async () => {
        if (isBattleRoyale) {
          // Battle Royale: skip team setup, go straight to actor picking
          await assignActorPickers(party.id)
          await updateParty(party.id, { status: 'actor_pick' })
        } else {
          // Other modes: proceed to team setup
          await updateParty(party.id, { status: 'team_setup' })
        }
      }
      return (
        <div className="party-director-phone">
          <span className="party-director-phone__badge" aria-hidden="true">
            <IconCrown size={24} />
          </span>
          <p className="party-director-phone__title">You're the Director!</p>
          <p className="party-director-phone__sub">
            {isBattleRoyale
              ? 'Let the competition begin! Ready?'
              : "You'll set up the teams and run the show. Ready?"}
          </p>
          <button
            className="btn-primary"
            style={{ marginTop: 16, padding: '14px 32px', fontSize: '1.1rem', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            onClick={handleContinue}
          >
            <span>Continue</span>
            <IconArrowRight size={18} aria-hidden="true" />
          </button>
        </div>
      )
    }

    return (
      <div className="party-player-screen">
        <p className="party-player-screen__headline" style={{ fontSize: '1.4rem' }}>
          The Director is being revealed...
        </p>
        <p className="party-player-screen__pulse">Stand by</p>
      </div>
    )
  }

  // ── STATUS: team_setup — new tap-to-place team builder ───────────────────
  if (party.status === 'team_setup') {
    return (
      <PartyTeamBuilder
        party={party}
        currentUserId={currentUserId}
        role={isDirector ? 'director' : 'player'}
      />
    )
  }

  // ── STATUS: team_locked — brief transition before actor_pick ─────────────
  if (party.status === 'team_locked') {
    return (
      <div className="party-player-screen">
        <p className="party-player-screen__headline" style={{ fontSize: '1.4rem' }}>
          Teams locked!
        </p>
        <p className="party-player-screen__pulse">Setting the stage...</p>
      </div>
    )
  }

  // ── STATUS: h2h-playing — SIMULTANEOUS turn-based team gameplay ──────────
  // All teams race in parallel. Each team has its OWN active player at any
  // given time, tracked by teamTurnIndices[teamId]. There is no global
  // "current team" — currentTeamIndex is ignored for role determination.
  if (party.status === 'h2h-playing') {
    const teams = Array.isArray(party.teams) ? party.teams : []
    const myTeam = teams.find((t) => (t.playerIds || []).includes(currentUserId))
    const myTeamTurnIdx = party.teamTurnIndices?.[myTeam?.id] ?? 0
    const activeUidForMyTeam = myTeam?.playerIds?.[myTeamTurnIdx] ?? null
    const isMyTurn = !!activeUidForMyTeam && activeUidForMyTeam === currentUserId
    return (
      <PartyH2HGameplay
        party={party}
        currentUserId={currentUserId}
        role={isMyTurn ? 'active' : 'watch'}
      />
    )
  }

  // ── STATUS: h2h-results ──────────────────────────────────────────────────
  if (party.status === 'h2h-results') {
    return (
      <>
        {celebrating && <Confetti />}
        <PartyH2HDone
          party={party}
          currentUserId={currentUserId}
          forHost={false}
          onBackToLobby={handleBackToLobby}
          onRefundMyTicket={onRefundMyTicket}
        />
      </>
    )
  }

  // ── (LEGACY) old inline team_setup — replaced above, kept dead so existing
  //    flows that hit this label still compile while wiring up. Safe to remove.
  if (false && party.status === 'team_setup') {
    if (isDirector) {
      const count = party.teamCount || selectedTeamCount

      const handleLockTeams = async () => {
        // Build teams object from current state
        const teamsObj = {}
        for (let i = 0; i < count; i++) {
          const id = `team_${i}`
          teamsObj[id] = { name: teamNames[id] || `Team ${i + 1}` }
        }
        await updateParty(party.id, { teams: teamsObj, teamsLocked: true })
      }

      const handleSetTeamCount = async (n) => {
        setSelectedTeamCount(n)
        const teamsObj = {}
        for (let i = 0; i < n; i++) {
          const id = `team_${i}`
          teamsObj[id] = { name: teamNames[id] || `Team ${i + 1}` }
        }
        await updateParty(party.id, { teamCount: n, teams: teamsObj })
      }

      const handleAssignPlayer = async (uid, teamId) => {
        await updateParty(party.id, { [`players.${uid}.teamId`]: teamId })
      }

      return (
        <div className="party-player-screen" style={{ justifyContent: 'flex-start', paddingTop: 40 }}>
          <p className="party-player-screen__headline" style={{ fontSize: '1.4rem' }}>
            Team Setup
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>You're the Director</p>

          {!count && (
            <>
              <p style={{ color: 'var(--text-primary)', marginTop: 16 }}>How many teams?</p>
              <div className="party-team-count-selector">
                {[2, 3, 4].map((n) => (
                  <button
                    key={n}
                    className={`party-team-count-btn${selectedTeamCount === n ? ' party-team-count-btn--selected' : ''}`}
                    onClick={() => handleSetTeamCount(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </>
          )}

          {count > 0 && (
            <>
              <p style={{ color: 'var(--text-primary)', marginTop: 16, marginBottom: 8 }}>
                Assign players to teams:
              </p>
              {playerEntries.map(([uid, p]) => {
                const currentTeam = p.teamId
                return (
                  <div key={uid} style={{ display: 'flex', gap: 8, alignItems: 'center', width: '100%', maxWidth: 340, marginBottom: 8 }}>
                    <span style={{ flex: 1, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                      {p.username || 'Guest'}
                    </span>
                    <select
                      style={{
                        background: 'var(--bg-card)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 6,
                        padding: '4px 8px',
                        fontSize: '0.85rem',
                      }}
                      value={currentTeam || ''}
                      onChange={(e) => handleAssignPlayer(uid, e.target.value || null)}
                    >
                      <option value="">No team</option>
                      {Array.from({ length: count }).map((_, i) => {
                        const id = `team_${i}`
                        return (
                          <option key={id} value={id}>
                            {teamNames[id] || `Team ${i + 1}`}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                )
              })}

              <button
                className="btn-primary"
                style={{ marginTop: 20, padding: '14px 32px' }}
                onClick={handleLockTeams}
              >
                Lock It In
              </button>
            </>
          )}
        </div>
      )
    }

    // Non-director: team join UI
    const teams = party.teams ? Object.entries(party.teams) : []
    const myTeamId = players[currentUserId]?.teamId

    const handleJoinTeam = async (teamId) => {
      await updateParty(party.id, { [`players.${currentUserId}.teamId`]: teamId })
    }

    return (
      <div className="party-player-screen">
        <p className="party-player-screen__headline" style={{ fontSize: '1.4rem' }}>
          Pick Your Team
        </p>

        {!party.teamCount && (
          <p className="party-player-screen__pulse">Waiting for Director...</p>
        )}

        {teams.map(([teamId, team], idx) => {
          const color = TEAM_COLORS[idx % TEAM_COLORS.length]
          const isMyTeam = myTeamId === teamId
          const members = Object.entries(players).filter(([, p]) => p.teamId === teamId)
          return (
            <div key={teamId} style={{ width: '100%', maxWidth: 320, marginBottom: 8 }}>
              <button
                className="party-team-join-btn"
                style={{
                  borderColor: color,
                  background: isMyTeam
                    ? `color-mix(in srgb, ${color} 20%, var(--bg-card))`
                    : 'var(--bg-card)',
                  color: isMyTeam ? color : 'var(--text-primary)',
                }}
                onClick={() => handleJoinTeam(teamId)}
              >
                {team.name || `Team ${idx + 1}`}
                {isMyTeam && (
                  <span style={{ marginLeft: 6, display: 'inline-flex', verticalAlign: 'middle' }} aria-label="Your team">
                    <IconCheck size={14} />
                  </span>
                )}
                {' '}({members.length})
              </button>
            </div>
          )
        })}

        {myTeamId && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 8 }}>
            You're in {party.teams?.[myTeamId]?.name || myTeamId}
          </p>
        )}
      </div>
    )
  }

  // ── STATUS: actor_pick — blind picks ──────────────────────────────────────
  if (party.status === 'actor_pick') {
    const isPicker0 = party.actorPickerIds?.[0] === currentUserId
    const isPicker1 = party.actorPickerIds?.[1] === currentUserId
    const isPicker  = isPicker0 || isPicker1

    const myPickField    = isPicker0 ? 'pick0' : 'pick1'
    const otherPickField = isPicker0 ? 'pick1' : 'pick0'
    const alreadyLocked  = isPicker && !!party[myPickField]

    // ── Non-picker: waiting screen ───────────────────────────────────────────
    if (!isPicker) {
      const pickers = party.actorPickerIds ?? []
      return (
        <div className="party-player-screen">
          <p className="party-player-screen__headline" style={{ fontSize: '1.4rem' }}>
            The cast is being chosen...
          </p>
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginTop: 8 }}>
            {pickers.map((uid, i) => {
              const p    = players[uid]
              const done = i === 0 ? !!party.pick0 : !!party.pick1
              const initial = (p?.username || '?').charAt(0).toUpperCase()
              return (
                <div key={uid} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div className="party-actor-pick__picker-avatar" style={{ width: 48, height: 48 }}>
                    {p?.avatar
                      ? <img src={p.avatar} alt={p.username} />
                      : <span className="party-avatar__initial">{initial}</span>}
                  </div>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {done
                      ? <span className="party-ready-check" aria-label="Done"><IconCheck size={12} /></span>
                      : <span className="party-ready-dot" aria-label="Waiting" />}
                    <span>{p?.username || 'Guest'}</span>
                  </span>
                </div>
              )
            })}
          </div>
          <p className="party-player-screen__pulse" style={{ marginTop: 16 }}>Stand by</p>
        </div>
      )
    }

    // ── Already locked in: waiting for co-star ───────────────────────────────
    if (alreadyLocked) {
      return (
        <div className="party-player-screen">
          <p className="party-player-screen__headline" style={{ fontSize: '1.4rem' }}>
            Locked in!
          </p>
          <p className="party-player-screen__pulse">Waiting for your co-star...</p>
        </div>
      )
    }

    // ── Saving spinner ───────────────────────────────────────────────────────
    if (actorPickDone) {
      return (
        <div className="party-player-screen">
          <p className="party-player-screen__pulse">Saving your pick...</p>
        </div>
      )
    }

    // ── Blind picker ─────────────────────────────────────────────────────────
    return (
      <ActorPicker
        slotRole="start"
        goalActor={null}
        blindPick
        onConfirm={async (actor) => {
          setActorPickDone(true)
          const pick = { id: actor.id, name: actor.name, profile_path: actor.profile_path ?? null }
          const otherPick = party[otherPickField]

          if (otherPick) {
            // Both picks are in — this device assigns start/goal randomly and triggers reveal
            assignmentFiredRef.current = true
            const coin = Math.random() > 0.5
            await updateParty(party.id, {
              startActor: coin ? otherPick : pick,
              goalActor:  coin ? pick : otherPick,
              pick0: null,
              pick1: null,
              status: 'reveal',
            })
          } else {
            // Co-star hasn't locked in yet — save pick and wait
            // picker 0's backup watcher effect handles the final assignment
            await updateParty(party.id, { [myPickField]: pick })
          }
        }}
        onCancel={null}
        confirmLabel="Lock In Actor"
      />
    )
  }

  // ── STATUS: trilogy-playing — a still-racing player sees "starting round"
  // briefly until onGameStart launches the loop; once they've finished (or DNF'd)
  // they watch the rest of the round on the read-only live board (same layout as
  // the host) instead of a blank "Starting round...". ──
  if (party.status === 'trilogy-playing') {
    const me = party.players?.[currentUserId]
    if (me?.status === 'finished' || me?.status === 'dnf') {
      return <PartyTrilogyGameplay party={party} readOnly />
    }
    return (
      <div className="party-player-screen">
        <p className="party-player-screen__pulse">Starting round...</p>
      </div>
    )
  }

  // ── STATUS: round_standings — delegate to shared component ──────────────
  if (party.status === 'round_standings') {
    return (
      <PartyTrilogyStandings
        party={party}
        isHost={false}
        onBackToLobby={handleBackToLobby}
      />
    )
  }

  // ── STATUS: final_standings — delegate to shared component ───────────────
  if (party.status === 'final_standings') {
    return (
      <PartyTrilogyFinal
        party={party}
        isHost={false}
        onBackToLobby={handleBackToLobby}
      />
    )
  }

  // ── STATUS: instructions — fires between reveal and LCA on all modes ─────
  if (party.status === 'instructions') {
    return <PartyInstructions party={party} role="player" />
  }

  // ── STATUS: reveal — cinematic reveal on all devices ──────────────────────
  if (party.status === 'reveal') {
    return (
      <PartyRevealScreen
        startActor={party.startActor}
        goalActor={party.goalActor}
      />
    )
  }

  // ── STATUS: reaction_test — Co-Op only ────────────────────────────────────
  if (party.status === 'reaction_test') {
    return (
      <PartyReactionTest
        party={party}
        currentUserId={currentUserId}
        forHost={false}
      />
    )
  }

  // ── STATUS: co-op-playing — turn-based gameplay ───────────────────────────
  if (party.status === 'co-op-playing') {
    const turnOrder = party.turnOrder ?? []
    const turnIndex = party.currentTurnIndex ?? 0
    const isMyTurn  = turnOrder[turnIndex] === currentUserId
    return (
      <PartyCoopGameplay
        party={party}
        currentUserId={currentUserId}
        role={isMyTurn ? 'active' : 'watch'}
      />
    )
  }

  // ── STATUS: coop-results — collective results screen ──────────────────────
  if (party.status === 'coop-results') {
    return (
      <>
        {celebrating && <Confetti />}
        <PartyCoopDone
          party={party}
          currentUserId={currentUserId}
          forHost={false}
          onBackToLobby={handleBackToLobby}
          onRefundMyTicket={onRefundMyTicket}
        />
      </>
    )
  }

  // ── STATUS: countdown — "Lights... Camera... Action!" sync ─────────────────
  if (party.status === 'countdown') {
    return <LightsCameraAction />
  }

  // ── STATUS: playing ────────────────────────────────────────────────────────
  // The useEffect above fires onGameStart which transitions App.jsx to gameplay.
  // This renders briefly while that transition happens.
  if (party.status === 'playing') {
    return (
      <div className="party-player-screen">
        <p className="party-player-screen__pulse">Starting game...</p>
      </div>
    )
  }

  // ── STATUS: ended ──────────────────────────────────────────────────────────
  if (party.status === 'ended') {
    return (
      <div className="party-player-screen">
        <p className="party-player-screen__headline">Thanks for playing!</p>
        <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>
          The host has ended the party.
        </p>
        <button
          className="btn-primary"
          onClick={onLeave}
          style={{ marginTop: 24, display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <IconHome size={18} aria-hidden="true" />
          <span>Back to Home</span>
        </button>
      </div>
    )
  }

  // ── STATUS: results ───────────────────────────────────────────────────────
  if (party.status === 'results') {
    const players = party.players ?? {}
    const myResult = players[currentUserId]

    // Rank players: finished first (by hops asc, time asc), then DNF/playing
    const ranked = Object.entries(players)
      .filter(([, p]) => p.status === 'finished')
      .sort((a, b) => {
        const diff = (a[1].hops ?? 999) - (b[1].hops ?? 999)
        if (diff !== 0) return diff
        return (a[1].time ?? 9999) - (b[1].time ?? 9999)
      })

    const myRank = ranked.findIndex(([uid]) => uid === currentUserId) + 1
    const winnerName = ranked[0]?.[1]?.username ?? 'Unknown'

    return (
      <div className="party-player-screen" style={{ justifyContent: 'flex-start', paddingTop: 32 }}>
        <p className="party-player-screen__headline" style={{ fontSize: '1.6rem', display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
          {myRank === 1 ? (
            <>
              <IconTrophy size={24} aria-hidden="true" />
              <span>You Win!</span>
            </>
          ) : myRank > 0
            ? `#${myRank} — Nice work!`
            : 'Game Over'}
        </p>

        {myResult?.status === 'finished' ? (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <p style={{ color: 'var(--accent-gold)', fontSize: '1.3rem', fontWeight: 800 }}>
              {myResult.hops} hop{myResult.hops !== 1 ? 's' : ''}
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              {fmtTime(myResult.time)}
            </p>
          </div>
        ) : (
          <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>You didn't finish in time</p>
        )}

        {myRank !== 1 && ranked.length > 0 && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 12 }}>
            Winner: {winnerName} ({ranked[0]?.[1]?.hops} hops)
          </p>
        )}

        <p className="party-player-screen__pulse" style={{ marginTop: 24 }}>
          Waiting for host...
        </p>
      </div>
    )
  }

  // Fallback
  return (
    <div className="party-player-screen">
      <p style={{ color: 'var(--text-muted)' }}>
        Party status: {party.status}
      </p>
      <button className="btn-ghost" onClick={onLeave} style={{ marginTop: 16 }}>
        Leave Party
      </button>
    </div>
  )
}

/**
 * Fetch everything a phone needs to actually play this round, so its readiness
 * ack means "I can play" — not merely "I saw the countdown". Chain modes (BR /
 * Speed Run / Co-Op / H2H / Trilogy) need the start actor's filmography to make
 * the first move; that fetch is cached, so gameplay then starts instantly.
 * Director's Cut is nomination-based off the live doc, so the doc alone suffices.
 * Retries briefly; resolves false if the data never loads (host timeout covers it).
 */
async function prefetchGameplayData(party) {
  const startId = party?.startActor?.id
  if (party?.mode === 'directors-cut' || startId == null) return true
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await getActorMovies(startId)
      return true
    } catch {
      if (attempt < 2) await new Promise((r) => setTimeout(r, 400 * (attempt + 1)))
    }
  }
  return false
}

/**
 * "Lights... Camera... Action!" cinematic sync screen for player phones.
 * Fires independently on each device when status='countdown' arrives; the device
 * also acks lcaReady (see the readiness effect in PartyPlayerScreen) so the host
 * advances everyone into gameplay together.
 */
function LightsCameraAction() {
  const [phase, setPhase] = useState('lights') // 'lights' | 'camera' | 'action'

  useEffect(() => {
    if (phase === 'lights') {
      const id = setTimeout(() => setPhase('camera'), 1200)
      return () => clearTimeout(id)
    }
    if (phase === 'camera') {
      const id = setTimeout(() => setPhase('action'), 1200)
      return () => clearTimeout(id)
    }
    // 'action' stays visible — gameplay will take over when status='playing' arrives
  }, [phase])

  return (
    <div className="party-lca-screen">
      <div className="party-lca-screen__words">
        <span className={`party-lca-word${phase === 'lights' || phase === 'camera' || phase === 'action' ? ' party-lca-word--visible' : ''}`}>
          Lights...
        </span>
        <span className={`party-lca-word${phase === 'camera' || phase === 'action' ? ' party-lca-word--visible' : ''}`}>
          Camera...
        </span>
        <span className={`party-lca-word party-lca-word--action${phase === 'action' ? ' party-lca-word--visible' : ''}`}>
          Action!
        </span>
      </div>
    </div>
  )
}
